/**
 * Pipeline Template Auto-Mapping Service
 * 
 * This service automatically generates default pipeline mappings for new clients
 * based on intelligent matching between:
 * - Pipeline stage names (from HighLevel)
 * - Appointment type names (from 4D EMR)
 * 
 * The matching uses keyword-based heuristics to create sensible defaults.
 */

import { supabase } from '../lib/supabase';

// Appointment statuses that indicate different stages of the journey
const BOOKING_STATUSES = ['unconfirmed', 'confirmed'];
const IN_PROGRESS_STATUSES = ['arrived', 'roomed', 'seen'];
const COMPLETED_STATUSES = ['checked_out', 'completed'];
const CANCELLED_STATUSES = ['cancelled', 'cancelled_by_office', 'cancelled_by_patient'];
const NO_SHOW_STATUSES = ['no_show', 'left_unseen'];
const RESCHEDULED_STATUSES = ['rescheduled_by_office', 'rescheduled_by_patient'];

// Keywords for matching appointment types to journey stages
const KEYWORDS = {
  // Types that start a new journey (create_new_opportunity = true)
  newJourney: {
    consult: ['consult', 'consultation', 'meet & greet', 'meet and greet'],
    pcc: ['pcc', 'pre-consultation'],
  },
  // Types that indicate surgery booking
  surgery: ['surgery', 'in or', 'or ', 'operating room', 'surgical procedure'],
  // Types that indicate pre-operative
  preOp: ['pre-op', 'preop', 'pre-operative', 'preoperative', 'pre op'],
  // Types that indicate post-operative
  postOp: ['post-op', 'postop', 'post-operative', 'postoperative', 'post op'],
  // Types that should be skipped (don't create/move opportunities)
  skip: [
    'follow-up', 'follow up', 'followup',
    'injectable', 'botox', 'filler',
    'laser', 'halo', 'bbl', 'profractional',
    'facial', 'peel', 'treatment',
    'admin', 'administration', 'test',
    'numbing', 'drip', 'iv ',
    'microneedling', 'hydra',
  ],
};

// Stage name patterns for matching
const STAGE_PATTERNS = {
  newLead: ['new lead', 'inquiry', 'interested'],
  coldFeet: ['cold feet', 'follow-up', 'reschedule'],
  pccScheduled: ['pcc', 'pre-consultation', 'call scheduled'],
  pccTime: ['time of pcc', 'pcc time', 'call time'],
  consultPending: ['consult pending', 'surgeon consult pending', 'pending consult'],
  consultBooked: ['consult booked', 'surgeon consult booked', 'consultation booked'],
  consultCompleted: ['consult completed', 'surgeon consult completed', 'consultation completed'],
  surgeryBooked: ['surgery booked', 'procedure booked', 'booked surgery'],
  surgeryCompleted: ['surgery completed', 'procedure completed', 'completed surgery'],
  reviewRequest: ['review', 'send review'],
};

interface Pipeline {
  hl_pipeline_id: string;
  name: string;
  stages: Array<{
    id: string;
    name: string;
    position: number;
  }>;
}

interface AppointmentType {
  external_id: number;
  name: string;
  is_active: boolean;
}

interface MappingTemplate {
  appointment_type_external_id: number;
  procedure_external_id: number | null;
  appointment_status: string;
  patient_status: string | null;
  hl_pipeline_id: string | null;
  hl_pipeline_stage_id: string | null;
  opportunity_status: string | null;
  skip_opportunity: boolean;
  create_new_opportunity: boolean;
  stage_order: number;
  priority_order: number;
}

/**
 * Matches a stage name against pattern keywords
 */
function matchStagePattern(stageName: string, patterns: string[]): boolean {
  const lower = stageName.toLowerCase();
  return patterns.some(p => lower.includes(p));
}

/**
 * Finds the best matching stage for a given pattern
 */
function findStageByPattern(stages: Pipeline['stages'], patterns: string[]): Pipeline['stages'][0] | null {
  for (const stage of stages) {
    if (matchStagePattern(stage.name, patterns)) {
      return stage;
    }
  }
  return null;
}

/**
 * Determines the type category for an appointment type based on its name
 */
function categorizeAppointmentType(typeName: string): 'pcc_consult' | 'surgeon_consult' | 'surgery' | 'pre_op' | 'post_op' | 'skip' {
  const lower = typeName.toLowerCase();
  
  // Check for skip types first (most common)
  if (KEYWORDS.skip.some(k => lower.includes(k))) {
    return 'skip';
  }
  
  // Check for surgery
  if (KEYWORDS.surgery.some(k => lower.includes(k))) {
    return 'surgery';
  }
  
  // Check for pre-op
  if (KEYWORDS.preOp.some(k => lower.includes(k))) {
    return 'pre_op';
  }
  
  // Check for post-op
  if (KEYWORDS.postOp.some(k => lower.includes(k))) {
    return 'post_op';
  }
  
  // Check for PCC consult (before general consult check)
  if (KEYWORDS.newJourney.pcc.some(k => lower.includes(k))) {
    return 'pcc_consult';
  }
  
  // Check for surgeon consult
  if (KEYWORDS.newJourney.consult.some(k => lower.includes(k))) {
    // Exclude non-surgical consultations
    if (lower.includes('non-surgical') || lower.includes('skin') || lower.includes('recovery')) {
      return 'skip';
    }
    return 'surgeon_consult';
  }
  
  // Default to skip for unknown types
  return 'skip';
}

/**
 * Helper to create a valid mapping that satisfies database constraints
 * - If skip_opportunity is false, pipeline and stage must be non-null
 * - If pipeline/stage is null, force skip_opportunity to true
 */
function createValidMapping(
  typeExternalId: number,
  status: string,
  pipelineId: string | null,
  stageId: string | null,
  oppStatus: string | null,
  skipOpp: boolean,
  createNew: boolean,
  stageOrder: number
): MappingTemplate {
  // Constraint: if skip_opportunity is false, pipeline and stage must be set
  // If we don't have valid pipeline/stage, force skip
  const hasValidPipelineStage = pipelineId && stageId && pipelineId.trim() !== '' && stageId.trim() !== '';
  const shouldSkip = skipOpp || !hasValidPipelineStage;
  
  return {
    appointment_type_external_id: typeExternalId,
    procedure_external_id: null,
    appointment_status: status,
    patient_status: null,
    hl_pipeline_id: shouldSkip ? null : pipelineId,
    hl_pipeline_stage_id: shouldSkip ? null : stageId,
    opportunity_status: shouldSkip ? null : oppStatus,
    skip_opportunity: shouldSkip,
    create_new_opportunity: shouldSkip ? false : createNew,
    stage_order: shouldSkip ? 0 : stageOrder,
    priority_order: 0,
  };
}

/**
 * Generates default mappings for a single appointment type
 */
function generateMappingsForType(
  type: AppointmentType,
  pipeline: Pipeline,
  stages: {
    coldFeet: Pipeline['stages'][0] | null;
    pccScheduled: Pipeline['stages'][0] | null;
    pccTime: Pipeline['stages'][0] | null;
    consultPending: Pipeline['stages'][0] | null;
    consultBooked: Pipeline['stages'][0] | null;
    consultCompleted: Pipeline['stages'][0] | null;
    surgeryBooked: Pipeline['stages'][0] | null;
    surgeryCompleted: Pipeline['stages'][0] | null;
  }
): MappingTemplate[] {
  const mappings: MappingTemplate[] = [];
  const category = categorizeAppointmentType(type.name);
  
  // Skip types - all statuses skip opportunity
  if (category === 'skip') {
    const allStatuses = [...BOOKING_STATUSES, ...IN_PROGRESS_STATUSES, ...COMPLETED_STATUSES, ...CANCELLED_STATUSES, ...NO_SHOW_STATUSES];
    for (const status of allStatuses) {
      mappings.push(createValidMapping(type.external_id, status, null, null, null, true, false, 0));
    }
    return mappings;
  }
  
  // PCC Consult types
  if (category === 'pcc_consult') {
    const pccStageId = stages.pccScheduled?.id || stages.consultBooked?.id || null;
    const pccTimeStageId = stages.pccTime?.id || stages.pccScheduled?.id || null;
    const consultPendingStageId = stages.consultPending?.id || stages.consultCompleted?.id || null;
    
    // Booking statuses → PCC Scheduled.
    // Reuse an existing open inquiry card when one exists; the sync creates a
    // new card when there is no open card (for example, after a completed journey).
    for (const status of BOOKING_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, pccStageId, 'open', false, false, stages.pccScheduled?.position || 3));
    }
    // In-progress → PCC Time
    for (const status of IN_PROGRESS_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, pccTimeStageId, 'open', false, false, stages.pccTime?.position || 4));
    }
    // Completed → Consult Pending
    for (const status of COMPLETED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, consultPendingStageId, 'open', false, false, stages.consultPending?.position || 5));
    }
    // No-show → Cold Feet (or skip if no cold feet stage)
    for (const status of NO_SHOW_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.coldFeet?.id || null, 'open', false, false, stages.coldFeet?.position || 2));
    }
    // Cancelled → skip (we don't have a "lost" stage to move to)
    for (const status of CANCELLED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, null, null, null, true, false, 0));
    }
    return mappings;
  }
  
  // Surgeon Consult types
  if (category === 'surgeon_consult') {
    // Booking statuses → Consult Booked.
    // Default to reusing an open inquiry card; users can explicitly opt into
    // "always create new" for workflows that intentionally run parallel journeys.
    for (const status of BOOKING_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.consultBooked?.id || null, 'open', false, false, stages.consultBooked?.position || 6));
    }
    // In-progress → Consult Booked
    for (const status of IN_PROGRESS_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.consultBooked?.id || null, 'open', false, false, stages.consultBooked?.position || 6));
    }
    // Completed → Consult Completed
    for (const status of COMPLETED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.consultCompleted?.id || null, 'open', false, false, stages.consultCompleted?.position || 7));
    }
    // No-show → Cold Feet (or skip if no cold feet stage)
    for (const status of NO_SHOW_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.coldFeet?.id || null, 'open', false, false, stages.coldFeet?.position || 2));
    }
    // Cancelled → skip
    for (const status of CANCELLED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, null, null, null, true, false, 0));
    }
    return mappings;
  }
  
  // Surgery types
  if (category === 'surgery') {
    // Booking statuses → Surgery Booked (create new if no existing opp)
    for (const status of BOOKING_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.surgeryBooked?.id || null, 'open', false, true, stages.surgeryBooked?.position || 8));
    }
    // In-progress → Surgery Booked
    for (const status of IN_PROGRESS_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.surgeryBooked?.id || null, 'open', false, false, stages.surgeryBooked?.position || 8));
    }
    // Completed → Surgery Completed (won)
    for (const status of COMPLETED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.surgeryCompleted?.id || null, 'won', false, false, stages.surgeryCompleted?.position || 9));
    }
    // No-show → Cold Feet (or skip if no cold feet stage)
    for (const status of NO_SHOW_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.coldFeet?.id || null, 'open', false, false, stages.coldFeet?.position || 2));
    }
    // Cancelled → skip
    for (const status of CANCELLED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, null, null, null, true, false, 0));
    }
    return mappings;
  }
  
  // Pre-op types (move existing opp to Surgery Booked)
  if (category === 'pre_op') {
    for (const status of [...BOOKING_STATUSES, ...IN_PROGRESS_STATUSES, ...COMPLETED_STATUSES]) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.surgeryBooked?.id || null, 'open', false, false, stages.surgeryBooked?.position || 8));
    }
    // Cancelled → skip (don't change opp status for pre-op cancel)
    for (const status of CANCELLED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, null, null, null, true, false, 0));
    }
    return mappings;
  }
  
  // Post-op types (move existing opp to Surgery Completed)
  if (category === 'post_op') {
    for (const status of [...BOOKING_STATUSES, ...IN_PROGRESS_STATUSES, ...COMPLETED_STATUSES]) {
      mappings.push(createValidMapping(type.external_id, status, pipeline.hl_pipeline_id, stages.surgeryCompleted?.id || null, 'won', false, false, stages.surgeryCompleted?.position || 9));
    }
    // Cancelled → skip
    for (const status of CANCELLED_STATUSES) {
      mappings.push(createValidMapping(type.external_id, status, null, null, null, true, false, 0));
    }
    return mappings;
  }
  
  return mappings;
}

/**
 * Main function to generate and save default pipeline mappings
 * 
 * @param userId - The user ID to generate mappings for
 * @param pipelineId - Optional specific pipeline ID (uses first pipeline if not specified)
 * @param overwrite - If true, deletes existing mappings first (default: false)
 * @returns The number of mappings created
 */
export async function generateDefaultPipelineMappings(
  userId: string,
  pipelineId?: string,
  overwrite: boolean = false
): Promise<{ success: boolean; mappingsCreated: number; error?: string }> {
  try {
    // Get the pipeline(s) for this user
    let pipelineQuery = supabase
      .from('hl_pipelines')
      .select('*')
      .eq('user_id', userId);
    
    if (pipelineId) {
      pipelineQuery = pipelineQuery.eq('hl_pipeline_id', pipelineId);
    }
    
    const { data: pipelines, error: pipelineError } = await pipelineQuery;
    
    if (pipelineError) throw pipelineError;
    if (!pipelines || pipelines.length === 0) {
      return { success: false, mappingsCreated: 0, error: 'No pipelines found for user' };
    }
    
    const pipeline = pipelines[0] as Pipeline;
    const stages = Array.isArray(pipeline.stages) ? pipeline.stages : [];
    
    // Find stages by pattern matching
    const stageMatches = {
      coldFeet: findStageByPattern(stages, STAGE_PATTERNS.coldFeet),
      pccScheduled: findStageByPattern(stages, STAGE_PATTERNS.pccScheduled),
      pccTime: findStageByPattern(stages, STAGE_PATTERNS.pccTime),
      consultPending: findStageByPattern(stages, STAGE_PATTERNS.consultPending),
      consultBooked: findStageByPattern(stages, STAGE_PATTERNS.consultBooked),
      consultCompleted: findStageByPattern(stages, STAGE_PATTERNS.consultCompleted),
      surgeryBooked: findStageByPattern(stages, STAGE_PATTERNS.surgeryBooked),
      surgeryCompleted: findStageByPattern(stages, STAGE_PATTERNS.surgeryCompleted),
    };
    
    console.log('Stage matches:', Object.fromEntries(
      Object.entries(stageMatches).map(([k, v]) => [k, v?.name || 'NOT FOUND'])
    ));
    
    // Get appointment types for this user
    const { data: appointmentTypes, error: typesError } = await supabase
      .from('appointment_types')
      .select('external_id, name, is_active')
      .eq('user_id', userId)
      .eq('is_active', true);
    
    if (typesError) throw typesError;
    if (!appointmentTypes || appointmentTypes.length === 0) {
      return { success: false, mappingsCreated: 0, error: 'No appointment types found for user' };
    }
    
    // Check for existing mappings
    const { count: existingCount } = await supabase
      .from('appointment_pipeline_mappings')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    if (existingCount && existingCount > 0 && !overwrite) {
      return { 
        success: false, 
        mappingsCreated: 0, 
        error: `User already has ${existingCount} mappings. Set overwrite=true to replace them.`
      };
    }
    
    // Delete existing mappings if overwriting
    if (overwrite && existingCount && existingCount > 0) {
      await supabase
        .from('appointment_pipeline_mappings')
        .delete()
        .eq('user_id', userId);
    }
    
    // Generate mappings for each appointment type
    const allMappings: Array<MappingTemplate & { user_id: string }> = [];
    
    for (const type of appointmentTypes) {
      const typeMappings = generateMappingsForType(type, pipeline, stageMatches);
      for (const mapping of typeMappings) {
        allMappings.push({
          ...mapping,
          user_id: userId,
        });
      }
    }
    
    // Insert mappings in batches
    const batchSize = 100;
    let inserted = 0;
    
    for (let i = 0; i < allMappings.length; i += batchSize) {
      const batch = allMappings.slice(i, i + batchSize);
      const { error: insertError } = await supabase
        .from('appointment_pipeline_mappings')
        .insert(batch);
      
      if (insertError) {
        console.error(`Error inserting batch ${Math.floor(i / batchSize) + 1}:`, insertError);
        throw insertError;
      }
      
      inserted += batch.length;
    }
    
    return { success: true, mappingsCreated: inserted };
    
  } catch (error) {
    console.error('Error generating default pipeline mappings:', error);
    return { 
      success: false, 
      mappingsCreated: 0, 
      error: error instanceof Error ? error.message : String(error) 
    };
  }
}

/**
 * Check if a user needs default mappings (has pipelines but no mappings)
 */
export async function checkNeedsDefaultMappings(userId: string): Promise<boolean> {
  const { count: pipelineCount } = await supabase
    .from('hl_pipelines')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);
  
  const { count: mappingCount } = await supabase
    .from('appointment_pipeline_mappings')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);
  
  return (pipelineCount ?? 0) > 0 && (mappingCount ?? 0) === 0;
}
